import React, { useRef, useEffect, useState } from 'react';
import api from '../../api/axios';
import { ChevronDown, DownloadIcon } from 'lucide-react';
import Popup from "../popup/Popup";
import QRDesignerComponent from '../../modules/qr-editor/QREditor';

const Layout1Form = ({ data, onChange, errors }) => {
  const { video, profile, product, brand } = data;
  const [campaigns, setCampaigns] = useState([]);
  const [openQrCodePopup, setOpenQrCodePopup] = useState(false);

  const fieldRefs = useRef({});

  useEffect(() => {
    if (!errors) return;

    const firstErrorKey = Object.keys(errors)[0];
    const el = fieldRefs.current[firstErrorKey];

    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [errors]);

  const handleChange = (section, field, value) => {
    let cleanedValue = value;

    if (typeof value === "string") {
      cleanedValue = (value || '').replace(/%/g, "");
    }

    if (field === 'url' || field === 'logoUrl' || field === 'imageUrl' || field === 'qrCodeUrl') {
      cleanedValue = value;
    }
    onChange('layout1', section, field, cleanedValue);
  };

  const getCampaigns = async () => {
    try {
      const response = await api.get('campaign/list');
      if (response && response.data && response.data.data) {
        setCampaigns(response.data.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getCampaigns();
  }, []);

  return (
    <div className="layout1-form">
      {/* Canvas Settings */}
      <div className="form-section">

        <div className="form-group">
          <label>Campaign Name</label>
          <div className='relative'>
            <select
              ref={(el) => (fieldRefs.current["camp_name"] = el)}
              value={data.camp_name}
              onChange={(e) => handleChange(null, 'camp_name', e.target.value)}
              className={`appearance-none ${errors?.camp_name ? "has-error" : ""} `}
            >
              <option value="">Select Campaign</option>
              {campaigns.map((camp) => (
                <option key={camp.camp_id} value={camp.camp_name}>
                  {camp.camp_name}
                </option>
              ))}
            </select>
            <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none" />
          </div>
          {errors?.camp_name && (
            <p className="text-red-500 text-sm mt-1">
              {errors.camp_name}
            </p>
          )}
        </div>

        <div className="form-group">
          <label>Brand Name</label>
          <input
            type="text"
            ref={(el) => (fieldRefs.current["brand_name"] = el)}
            value={data.brand_name || ''}
            onChange={(e) => handleChange(null, 'brand_name', e.target.value)}
            className={errors?.brand_name ? "has-error" : ""}
          />
          {errors?.brand_name && (
            <p className="text-red-500 text-sm mt-1">
              {errors.brand_name}
            </p>
          )}
        </div>

        <div className="form-group">
          <label>Domain</label>
          <input
            type="text"
            ref={(el) => (fieldRefs.current["domain"] = el)}
            value={data.domain || ''}
            onChange={(e) => handleChange(null, 'domain', e.target.value)}
            className={errors?.domain ? "has-error" : ""}
          />
          {errors?.domain && (
            <p className="text-red-500 text-sm mt-1">
              {errors.domain}
            </p>
          )}
        </div>

        <div className="form-group">
          <label>Creative Name</label>
          <input
            type="text"
            ref={(el) => (fieldRefs.current["creative_name"] = el)}
            value={data.creative_name || ''}
            onChange={(e) => handleChange(null, 'creative_name', e.target.value)}
            className={errors?.creative_name ? "has-error" : ""}
          />
          {errors?.creative_name && (
            <p className="text-red-500 text-sm mt-1">
              {errors.creative_name}
            </p>
          )}
        </div>

        <div className="form-group hidden">
          <label>Background Type</label>
          <div className='relative'>
            <select
              ref={(el) => (fieldRefs.current["backgroundType"] = el)}
              value={data.canvas?.backgroundType || 'solid'}
              onChange={(e) => handleChange('canvas', 'backgroundType', e.target.value)}
              className='appearance-none'
            >
              <option value="solid">Solid</option>
              <option value="gradient">Gradient</option>
            </select>
            <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none" />
          </div>
        </div>

        {data.canvas?.backgroundType === 'gradient' && (
          <>
            <div className="form-group">
              <label>Gradient Color 1</label>
              <div className="color-input-group">
                <input
                  type="color"
                  value={data.canvas?.gradientColor1 || data.canvas?.backgroundColor || '#e0e0e0'}
                  onChange={(e) => handleChange('canvas', 'gradientColor1', e.target.value)}
                  ref={(el) => (fieldRefs.current["gradientColor1"] = el)}
                />
                <input
                  type="text"
                  value={data.canvas?.gradientColor1 || data.canvas?.backgroundColor || '#e0e0e0'}
                  onChange={(e) => handleChange('canvas', 'gradientColor1', e.target.value)}
                  placeholder="#e0e0e0"
                  className="color-text-input"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Gradient Color 2</label>
              <div className="color-input-group">
                <input
                  type="color"
                  value={data.canvas?.gradientColor2 || '#ffffff'}
                  onChange={(e) => handleChange('canvas', 'gradientColor2', e.target.value)}
                  ref={(el) => (fieldRefs.current["gradientColor2"] = el)}
                />
                <input
                  type="text"
                  value={data.canvas?.gradientColor2 || '#ffffff'}
                  onChange={(e) => handleChange('canvas', 'gradientColor2', e.target.value)}
                  placeholder="#ffffff"
                  className="color-text-input"
                />
              </div>
            </div>
            <div className="form-group hidden">
              <label>Gradient Angle (deg)</label>
              <input
                type="number"
                min="0"
                max="360"
                value={data.canvas?.gradientAngle ?? 135}
                onChange={(e) => handleChange('canvas', 'gradientAngle', parseInt(e.target.value, 10) || 0)}
                ref={(el) => (fieldRefs.current["gradientAngle"] = el)}
              />
            </div>
          </>
        )}
        <div className="form-group hidden">
          <label>Padding (px)</label>
          <input
            type="text"
            value={data.canvas?.padding || 20}
            onChange={(e) => handleChange('canvas', 'padding', e.target.value || 0)}
            ref={(el) => (fieldRefs.current["padding"] = el)}
          />
        </div>
      </div>

      {/* Video Section */}
      <div className="form-section">
        <h3>Canvas and video settings</h3>
        {(data.canvas?.backgroundType || 'solid') === 'solid' && (
          <>
            <div className="form-group">
              <label>Background Color</label>
              <div className="color-input-group">
                <input
                  type="color"
                  value={data.canvas?.backgroundColor || '#e0e0e0'}
                  onChange={(e) => handleChange('canvas', 'backgroundColor', e.target.value)}
                  ref={(el) => (fieldRefs.current["backgroundColor"] = el)}
                />
                <input
                  type="text"
                  value={data.canvas?.backgroundColor || '#e0e0e0'}
                  onChange={(e) => handleChange('canvas', 'backgroundColor', e.target.value)}
                  placeholder="#e0e0e0"
                  className="color-text-input"
                  ref={(el) => (fieldRefs.current["backgroundColorText"] = el)}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Text Color</label>
              <div className="color-input-group">
                <input
                  type="color"
                  value={data.canvas?.textColor || '#e0e0e0'}
                  onChange={(e) => handleChange('canvas', 'textColor', e.target.value)}
                  ref={(el) => (fieldRefs.current["textColor"] = el)}
                />
                <input
                  type="text"
                  value={data.canvas?.textColor || '#e0e0e0'}
                  onChange={(e) => handleChange('canvas', 'textColor', e.target.value)}
                  placeholder="#e0e0e0"
                  className="color-text-input"
                  ref={(el) => (fieldRefs.current["textColorText"] = el)}
                />
              </div>
            </div>
          </>
        )}

        <div className="form-group">
          <label>Video or Instagram URL</label>
          <input
            type="text"
            value={video.url}
            onChange={(e) => handleChange('video', 'url', e.target.value)}
            placeholder="Enter video URL"
            ref={(el) => (fieldRefs.current["url"] = el)}
          />
          <span className='text-xs text-blue-500'>Aspect ratio: 9:16 (vertical)</span>
        </div>
        <div className="form-row !hidden">
          <div className="form-group">
            <label>Width (%)</label>
            <input
              type="number"
              min="10"
              max="100"
              value={video.width || 100}
              onChange={(e) => handleChange('video', 'width', parseInt(e.target.value) || 100)}
              ref={(el) => (fieldRefs.current["width"] = el)}
            />
          </div>
          <div className="form-group">
            <label>Height (%)</label>
            <input
              type="number"
              min="10"
              max="100"
              value={video.height || 100}
              onChange={(e) => handleChange('video', 'height', parseInt(e.target.value) || 100)}
              ref={(el) => (fieldRefs.current["height"] = el)}
            />
          </div>
        </div>
        <div className="form-group hidden">
          <label>Video Shape</label>
          <div className='relative'>
            <select
              value={video.shape || 'rounded'}
              onChange={(e) => handleChange('video', 'shape', e.target.value)}
              className='appearance-none'
            >
              <option value="none">None</option>
              <option value="square">Square</option>
              <option value="rounded">Rounded</option>
              <option value="rounded-lg">Rounded Large</option>
              <option value="circle">Circle</option>
            </select>
            <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 pointer-events-none" />
          </div>
        </div>
        <div className="form-group hidden">
          <label>Shadow</label>
          <input
            type="text"
            value={video.shadow || '0 4px 20px rgba(0, 0, 0, 0.3)'}
            onChange={(e) => handleChange('video', 'shadow', e.target.value)}
            placeholder="0 4px 20px rgba(0, 0, 0, 0.3)"
          />
          <small className="form-hint">CSS box-shadow value</small>
        </div>

        {/* Caption with validation and info */}
        <div className="form-group">
          <label>Caption</label>
          <textarea
            value={video.caption}
            ref={(el) => (fieldRefs.current["caption"] = el)}
            onChange={(e) => handleChange('video', 'caption', e.target.value)}
            placeholder="Enter video caption"
            rows="3"
            minLength={3}
            maxLength={125}
            required
          />
          <p className="text-xs text-blue-500 mt-1">Min: 3 characters, Max: 125 characters</p>
          {errors?.caption && (
            <p className="text-red-500 text-sm mt-1">
              {errors.caption}
            </p>
          )}
        </div>

        <div className="form-group">
          <label>Hashtags</label>
          <input
            type="text"
            value={video.hashtags}
            onChange={(e) => handleChange('video', 'hashtags', e.target.value)}
            placeholder="#hashtag1 #hashtag2"
            ref={(el) => (fieldRefs.current["hashtags"] = el)}
          />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Likes</label>
            <input
              type="text"
              ref={(el) => (fieldRefs.current["likes"] = el)}
              value={video.likes}
              maxLength={5}
              onChange={(e) => handleChange('video', 'likes', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Comments</label>
            <input
              type="text"
              value={video.comments}
              maxLength={5}
              onChange={(e) => handleChange('video', 'comments', e.target.value)}
              ref={(el) => (fieldRefs.current["comments"] = el)}
            />
          </div>
          <div className="form-group">
            <label>Shares</label>
            <input
              type="text"
              maxLength={5}
              value={video.shares}
              onChange={(e) => handleChange('video', 'shares', e.target.value)}
              ref={(el) => (fieldRefs.current["shares"] = el)}
            />
          </div>
        </div>
      </div>

      {/* Profile Section */}
      <div className="form-section">
        <h3>Profile</h3>
        <div className="form-group">
          <label>Brand Logo URL</label>
          <input
            type="text"
            value={brand.logoUrl || ''}
            onChange={(e) => handleChange('brand', 'logoUrl', e.target.value)}
            placeholder="Enter logo image URL"
            ref={(el) => (fieldRefs.current["logoUrl"] = el)}
          />
          <p className="text-xs text-blue-500 mt-1">Min: 200x200, Max: 350x350 (square)</p>
        </div>

        <div className="form-group">
          <label>Brand Title</label>
          <input
            type="text"
            value={profile.brandTitle || ''}
            onChange={(e) => { handleChange('profile', 'brandTitle', e.target.value); handleChange('profile', 'username', e.target.value) }}
            placeholder="Enter brand title"
            ref={(el) => (fieldRefs.current["brandTitle"] = el)}
            minLength={3}
            maxLength={35}
            required
          />
          <p className="text-xs text-blue-500 mt-1">Min: 3 characters, Max: 35 characters</p>
          {errors?.brandTitle && (
            <p className="text-red-500 text-sm mt-1">
              {errors.brandTitle}
            </p>
          )}
        </div>

        <div className="form-group hidden">
          <label>Username</label>
          <input
            type="text"
            value={profile.username}
            onChange={(e) => handleChange('profile', 'username', e.target.value)}
            placeholder="@username"
            ref={(el) => (fieldRefs.current["username"] = el)}
          />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Following</label>
            <input
              type="text"
              value={profile.following}
              maxLength={5}
              onChange={(e) => handleChange('profile', 'following', e.target.value)}
              ref={(el) => (fieldRefs.current["following"] = el)}
            />
          </div>
          <div className="form-group">
            <label>Followers</label>
            <input
              type="text"
              value={profile.followers}
              maxLength={5}
              onChange={(e) => handleChange('profile', 'followers', e.target.value)}
              ref={(el) => (fieldRefs.current["followers"] = el)}
            />
          </div>
          <div className="form-group">
            <label>Likes</label>
            <input
              type="text"
              value={profile.likes}
              maxLength={5}
              onChange={(e) => handleChange('profile', 'likes', e.target.value)}
              ref={(el) => (fieldRefs.current["likes"] = el)}
            />
          </div>
        </div>

        <div className="form-group">
          <label>Bio</label>
          <textarea
            value={profile.bio}
            onChange={(e) => handleChange('profile', 'bio', e.target.value.replace(/\n/g, ""))}
            placeholder="Enter profile bio"
            rows="4"
            minLength={100}
            maxLength={300}
            required
            ref={(el) => (fieldRefs.current["bio"] = el)}
          />
          <p className="text-xs text-blue-500 mt-1">Min: 100 characters, Max: 300 characters</p>
          {errors?.bio && (
            <p className="text-red-500 text-sm mt-1">
              {errors.bio}
            </p>
          )}
        </div>
      </div>

      {/* Product Section */}
      <div className="form-section">
        <h3>Product</h3>
        <div className="form-group">
          <label>Product Name</label>
          <input
            type="text"
            value={product.name}
            onChange={(e) => handleChange('product', 'name', e.target.value)}
            placeholder="Product name"
            ref={(el) => (fieldRefs.current["name"] = el)}
            minLength={3}
            maxLength={25}
            required
          />
          <p className="text-xs text-blue-500 mt-1">Min: 3 characters, Max: 25 characters</p>
          {errors?.name && (
            <p className="text-red-500 text-sm mt-1">
              {errors.name}
            </p>
          )}
        </div>
        <div className="form-group">
          <label>Price</label>
          <input
            type="text"
            value={product.price}
            onChange={(e) => handleChange('product', 'price', e.target.value)}
            placeholder="$0.00"
            ref={(el) => (fieldRefs.current["price"] = el)}
          />
        </div>
        <div className="form-group">
          <label>Rating (1-5)</label>
          <input
            type="number"
            min="1"
            max="5"
            value={product.rating}
            onChange={(e) => handleChange('product', 'rating', parseInt(e.target.value))}
            ref={(el) => (fieldRefs.current["rating"] = el)}
          />
        </div>
        <div className="form-group">
          <label>Product Image URL</label>
          <input
            type="text"
            value={product.imageUrl}
            onChange={(e) => handleChange('product', 'imageUrl', e.target.value)}
            placeholder="Enter image URL"
            ref={(el) => (fieldRefs.current["imageUrl"] = el)}
          />
          <p className="text-xs text-blue-500 mt-1">Must be 250x250 (square)</p>
        </div>
        <div className="form-group !mb-0">
          <label className='!flex justify-between cursor-pointer'
            onClick={() => setOpenQrCodePopup(true)}>QR Code URL <span className='hover:text-blue-800 mr-2'
            ><DownloadIcon className='h-4 w-4' /></span>
          </label>
          <input
            type="text"
            value={brand.qrCodeUrl}
            onChange={(e) => handleChange('brand', 'qrCodeUrl', e.target.value)}
            placeholder="Enter QR code URL"
          />
          <p className="text-xs text-blue-500 mt-1">Must be 250x250 (square)</p>
        </div>
      </div>
      <Popup isOpen={openQrCodePopup} onClose={() => setOpenQrCodePopup(false)}>
        <QRDesignerComponent />
      </Popup>
    </div>
  );
};

export default Layout1Form;